import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { usePermisos } from '../hooks/usePermisos';
import { MenuPageIntro, HeaderIOS, SeccionApps, TarjetaApp, IOS } from './shared/MenuLayout';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale,
  BarElement, Tooltip as CTooltip, Legend
} from 'chart.js';
import { Doughnut, Bar, Pie } from 'react-chartjs-2';
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, CTooltip, Legend);

const MID = 11;
const GREEN = '#34C759';
const COLORS = ['#007AFF','#34C759','#FF9500','#FF3B30','#5E5CE6','#5AC8FA','#FF2D55','#30D158'];

const Spin = ({color=IOS.blue}) => (
  <Box sx={{width:16,height:16,borderRadius:'50%',
    border:`2px solid ${color}30`,borderTopColor:color,
    animation:'spin 0.8s linear infinite',
    '@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/>
);

const Seguros = () => {
  const navigate   = useNavigate();
  const { tieneOpcion, cargando } = usePermisos();
  const user  = authService.getCurrentUser();
  const nc    = user?.nombreCompleto||user?.usuario||'';
  const cap   = t=>t.toLowerCase().split(' ').map(p=>p.charAt(0).toUpperCase()+p.slice(1)).join(' ');
  const nf    = cap(nc);
  const ueId  = user?.ueIdEstablecimiento||user?.UeIdEstablecimiento||0;
  const [hov, setHov] = useState(null);
  const logout = () => { authService.logout(); navigate('/login'); };

  const [motorData,    setMotorData]    = useState(null);
  const [syncData,     setSyncData]     = useState([]);
  const [ventasData,   setVentasData]   = useState([]);
  const [cargMotor,    setCargMotor]    = useState(true);
  const [cargSync,     setCargSync]     = useState(true);
  const [cargVentas,   setCargVentas]   = useState(true);
  const [tipoGrafico,  setTipoGrafico]  = useState('bar');

  useEffect(() => {
    api.get('/seguros/motor-reglas',     {params:{ueId}})
      .then(r=>{ if(r.data.success&&r.data.data?.length) setMotorData(r.data.data[0]); })
      .catch(console.error).finally(()=>setCargMotor(false));
    api.get('/seguros/sincronizacion',   {params:{ueId}})
      .then(r=>{ if(r.data.success) setSyncData(r.data.data||[]); })
      .catch(console.error).finally(()=>setCargSync(false));
    api.get('/seguros/ventas-forma-pago',{params:{ueId}})
      .then(r=>{ if(r.data.success) setVentasData(r.data.data||[]); })
      .catch(console.error).finally(()=>setCargVentas(false));
  },[ueId]);

  const opciones = [
    {id:'auditoria',       n:'Auditoría',      r:'/seguros/auditoria'},
    {id:'consumopac-sis', n:'Consumo Farmacia', r:'/seguros/consumo-paciente'},
    {id:'kardexfarmacia',  n:'Kardex Farmacia',r:'/seguros/kardexfarmacia'},
    {id:'validacionrc',    n:'Validación RC',  r:'/seguros/validacionrc'},
  ];

  // Chart data
  const dias     = useMemo(()=>[...new Set(ventasData.map(d=>d.dia))],[ventasData]);
  const formas   = useMemo(()=>[...new Set(ventasData.map(d=>d.formaPago))],[ventasData]);

  const chartData = useMemo(()=>{
    if(tipoGrafico==='donut'||tipoGrafico==='pie'){
      const totalesPorForma = formas.map(f=>
        ventasData.filter(d=>d.formaPago===f).reduce((s,d)=>s+Number(d.total||0),0)
      );
      return {
        labels: formas,
        datasets:[{
          data: totalesPorForma,
          backgroundColor: COLORS.slice(0,formas.length),
          borderWidth: 0,
          hoverOffset: 6,
        }]
      };
    }
    return {
      labels: dias,
      datasets: formas.map((f,i)=>({
        label: f,
        data: dias.map(dia=>{
          const row = ventasData.find(d=>d.dia===dia&&d.formaPago===f);
          return row ? Number(row.total) : 0;
        }),
        backgroundColor: COLORS[i%COLORS.length],
        borderRadius: 6,
        borderWidth: 0,
      }))
    };
  },[ventasData, dias, formas, tipoGrafico]);

  const chartOpts = useMemo(()=>({
    responsive:true, maintainAspectRatio:false,
    animation:{duration:500,easing:'easeOutQuart'},
    plugins:{
      datalabels:{display:false},
      legend:{position:'bottom',labels:{usePointStyle:true,pointStyle:'circle',
        padding:12,color:IOS.label2,font:{size:10}}},
      tooltip:{backgroundColor:'rgba(28,28,30,0.92)',titleColor:'#fff',
        bodyColor:'rgba(255,255,255,0.75)',padding:10,borderRadius:10,displayColors:true,boxPadding:3},
    },
    scales: tipoGrafico==='bar' ? {
      x:{grid:{display:false},ticks:{font:{size:9},color:IOS.label2},border:{display:false}},
      y:{grid:{color:'rgba(60,60,67,0.05)'},ticks:{font:{size:9},color:IOS.label2,maxTicksLimit:5},border:{display:false}},
    } : undefined,
    cutout: tipoGrafico==='donut'?'60%':undefined,
  }),[tipoGrafico]);

  const maxHora = 1;

  return (
    <Box className="integra-menu" sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
      <HeaderIOS titulo="Seguros" subtitulo=""
        onHome={()=>navigate('/dashboard')} onLogout={logout}
        nombreCompleto={nf} avatarLetra={nc.charAt(0).toUpperCase()}/>

      <Box sx={{maxWidth:1400,mx:'auto',px:{xs:2,sm:4},pt:3.5,pb:6}}>
        <Grid container spacing={2.5} className="integra-launcher-layout">

          {/* Izquierda — apps */}
          <Grid item xs={12} lg={8}>
        <MenuPageIntro title="Seguros" description="Consultas, cobertura y seguimiento de tus servicios." />
              <Grid container spacing={1.5}>
                {opciones.map(o=>(
                  <Grid item xs={4} sm={2} key={o.id}>
                    <TarjetaApp id={o.id} nombre={o.n} iconKey={o.id} ruta={o.r}
                      bloqueado={!cargando&&!tieneOpcion(o.id,MID)}
                      hoveredId={hov} onHover={setHov} onLeave={()=>setHov(null)}/>
                  </Grid>
                ))}
              </Grid>
          </Grid>

          {/* Derecha — cards */}
          <Grid item xs={12} lg={4} className="integra-launcher-cards" component="aside" aria-label="Resumen del módulo">
            <Box className="integra-launcher-card-group" sx={{display:'flex',flexDirection:'column',gap:1.8}}>

              {/* ── 1. Motor de Reglas ── */}
              <Box sx={{borderRadius:'20px',p:2.5,overflow:'hidden',position:'relative',
                bgcolor:'#1e9c4e',
                boxShadow:`0 2px 8px rgba(0,0,0,0.12)`}}>
                <Box sx={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',mb:2}}>
                  <Box>
                    <Typography sx={{fontSize: '0.72rem',fontWeight:700,color:'rgba(255,255,255,0.7)',
                      letterSpacing:'0.1em',textTransform:'uppercase',mb:0.3}}>Motor de Reglas</Typography>
                    {cargMotor
                      ? <Spin color="white"/>
                      : <Typography sx={{fontSize:'0.95rem',fontWeight:800,color:'white',lineHeight:1.3}}>
                          {motorData?.estado||'Sin datos'}
                        </Typography>
                    }
                  </Box>
                  <Box sx={{width:40,height:40,borderRadius:'14px',bgcolor:'rgba(255,255,255,0.2)',
                    display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </Box>
                </Box>
                {!cargMotor&&motorData&&(
                  <Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:1}}>
                    {[['Motor',motorData.motor],['Actualización',motorData.actualizacion]].map(([l,v])=>(
                      <Box key={l} sx={{bgcolor:'rgba(255,255,255,0.18)',borderRadius:'12px',p:1.2,
                        border:'1px solid rgba(255,255,255,0.2)'}}>
                        <Typography sx={{fontSize: '0.72rem',fontWeight:700,color:'rgba(255,255,255,0.65)',
                          textTransform:'uppercase',letterSpacing:'0.06em',mb:0.3}}>{l}</Typography>
                        <Typography sx={{fontSize:'0.78rem',fontWeight:700,color:'white'}}>{v}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {/* Decoración */}
                <Box sx={{position:'absolute',top:-20,right:-20,width:80,height:80,
                  borderRadius:'50%',bgcolor:'rgba(255,255,255,0.06)',pointerEvents:'none'}}/>
                <Box sx={{position:'absolute',bottom:-30,right:20,width:60,height:60,
                  borderRadius:'50%',bgcolor:'rgba(255,255,255,0.04)',pointerEvents:'none'}}/>
              </Box>

              {/* ── 2. Sincronización SISMED (modernizado y compacto) ── */}
<Box sx={{
  bgcolor: IOS.surface,
  borderRadius: '20px',
  p: 2,
  border: `1px solid ${IOS.separator}`,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
}}>
  {/* Header compacto */}
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5 }}>
    <Box sx={{
      width: 34, height: 34,
      borderRadius: '12px',
      bgcolor: `${GREEN}12`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round">
        <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
      </svg>
    </Box>
    <Box>
      <Typography sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: IOS.label2,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        mb: 0.1
      }}>
        Sincronización SISMED
      </Typography>
      {cargSync ? (
        <Spin color={GREEN} />
      ) : (
        <Typography sx={{
          fontSize: '0.78rem',
          fontWeight: 700,
          color: IOS.label
        }}>
          {syncData.length} servicios activos
        </Typography>
      )}
    </Box>
  </Box>

  {!cargSync && syncData.length > 0 && (() => {
    const ahoraH = new Date().getHours() + new Date().getMinutes() / 60;

    const extraerHora = (h) => {
      if (!h) return 0;
      const num = parseInt(h);
      const esPM = h.toUpperCase().includes('PM');
      const esAM = h.toUpperCase().includes('AM');
      if (esPM && num !== 12) return num + 12;
      if (esAM && num === 12) return 0;
      return num;
    };

    const getColor = (horaStr) => {
      const hora = extraerHora(horaStr);
      const diff = hora - ahoraH;
      if (diff < 0) return IOS.label2;
      if (diff < 1) return IOS.red;
      if (diff < 6) return IOS.orange;
      return GREEN;
    };

    const getLabel = (horaStr) => {
      const hora = extraerHora(horaStr);
      const diff = hora - ahoraH;
      if (diff < 0) return 'Completado';
      if (diff < 1) return `${Math.round(diff * 60)} min`;
      return `${Math.floor(diff)} h rest`;
    };

    const maxHora = Math.max(...syncData.map(d => extraerHora(d.horaVenta)), 1);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {syncData.map((s, i) => {
          const color = getColor(s.horaVenta);
          const label = getLabel(s.horaVenta);
          const hora = extraerHora(s.horaVenta);
          const pct = Math.min(Math.round((hora / maxHora) * 100), 100);
          const yaPaso = hora - ahoraH < 0;

          return (
            <Box
              key={i}
              sx={{
                p: 1,
                borderRadius: '14px',
                bgcolor: yaPaso ? `${color}06` : `${color}08`,
                border: `1px solid ${yaPaso ? IOS.separator : color + '25'}`,
                transition: 'all 0.2s'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: IOS.label,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    mr: 1
                  }}
                >
                  {s.nombre}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  px: 0.7,
                  py: 0.2,
                  borderRadius: '20px',
                  bgcolor: `${color}15`,
                  flexShrink: 0
                }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>{s.horaVenta}</Typography>
                </Box>
              </Box>

              {/* Barra de progreso más delgada */}
              <Box sx={{ height: 3, borderRadius: 3, bgcolor: `${color}15`, overflow: 'hidden', my: 0.6 }}>
                <Box sx={{
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: color,
                  width: `${pct}%`,
                  transition: 'width 0.6s ease'
                }} />
              </Box>

              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color, lineHeight: 1.2 }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  })()}
</Box>
              {/* ── 3. Ventas por Forma de Pago ── */}
              <Box sx={{bgcolor:IOS.surface,borderRadius:'20px',p:2.5,
                border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                <Box sx={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',mb:1.8}}>
                  <Box>
                    <Typography sx={{fontSize: '0.72rem',fontWeight:700,color:IOS.label2,
                      textTransform:'uppercase',letterSpacing:'0.08em',mb:0.2}}>Ventas por Forma de Pago</Typography>
                    <Typography sx={{fontSize:'0.75rem',color:IOS.label2}}>Últimos 5 días</Typography>
                  </Box>
                  {/* Selector tipo gráfico */}
                  <Box sx={{display:'flex',gap:0.4,bgcolor:IOS.bg,borderRadius:'10px',p:0.4,flexShrink:0}}>
                    {[
                      {key:'bar',   icon:<><rect x="3" y="10" width="4" height="8" rx="1" fill="currentColor"/><rect x="10" y="6" width="4" height="12" rx="1" fill="currentColor"/><rect x="17" y="3" width="4" height="15" rx="1" fill="currentColor"/></>},
                      {key:'donut', icon:<><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="5"/></>},
                      {key:'pie',   icon:<><path d="M12 2 A10 10 0 0 1 22 12 L12 12 Z" fill="currentColor"/><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/></>},
                    ].map(({key,icon})=>(
                      <Box component="button" type="button" aria-label={{bar:'Gráfico de barras',donut:'Gráfico de anillo',pie:'Gráfico circular'}[key]} aria-pressed={tipoGrafico===key} key={key} onClick={()=>setTipoGrafico(key)}
                        sx={{width:44,height:44,border:0,borderRadius:'10px',cursor:'pointer',
                          display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s',
                          bgcolor:tipoGrafico===key?IOS.surface:'transparent',
                          boxShadow:tipoGrafico===key?'0 1px 3px rgba(0,0,0,0.12)':'none',
                          color:tipoGrafico===key?GREEN:IOS.label2,
                          '&:hover':{bgcolor:tipoGrafico===key?IOS.surface:IOS.separator}}}>
                        <svg width="16" height="16" viewBox="0 0 24 24">{icon}</svg>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box key={tipoGrafico} sx={{height:210,animation:'fadeIn 0.3s ease'}}>
                  <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}`}</style>
                  {cargVentas
                    ? <Box sx={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <Spin color={GREEN}/>
                      </Box>
                    : ventasData.length===0
                      ? <Box sx={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <Typography sx={{fontSize:'0.78rem',color:IOS.label2}}>Sin datos</Typography>
                        </Box>
                      : tipoGrafico==='bar'
                        ? <Bar     data={chartData} options={chartOpts}/>
                        : tipoGrafico==='donut'
                          ? <Doughnut data={chartData} options={chartOpts}/>
                          : <Pie     data={chartData} options={chartOpts}/>
                  }
                </Box>
              </Box>

            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Seguros;